import API_URL from '../config';
const API = `${API_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('projabze_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// 401 geldiginde token temizle ve login'e yonlendir.
// Yalnizca okuma (GET) isteklerinde kullanilir; mutation'larda cagrilmaz.
function redirectOnExpiredToken() {
  localStorage.removeItem('projabze_token');
  localStorage.removeItem('projabze_user');
  window.location.href = '/login';
}

// Yanit govdesinden hata mesajini guvenlice cikar
async function extractError(res) {
  try {
    const data = await res.json();
    return data.message || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}: ${res.statusText}`;
  }
}

// ─── Project CRUD ───

export async function createProject(name) {
  const res = await fetch(`${API}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).project;
}

export async function listProjects() {
  const res = await fetch(`${API}/projects`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    // Oturum süresi dolduysa login sayfasına yönlendir
    if (res.status === 401) { redirectOnExpiredToken(); }
    throw new Error(await extractError(res));
  }
  return (await res.json()).projects;
}

export async function getProject(id) {
  const res = await fetch(`${API}/projects/${id}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).project;
}

export async function updateProject(id, data) {
  const res = await fetch(`${API}/projects/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).project;
}

export async function deleteProject(id) {
  const res = await fetch(`${API}/projects/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
}

// ─── Clients ───

export async function searchClients(query, filters = {}) {
  const params = new URLSearchParams({ q: query });
  if (filters.sectorRole) params.set('sectorRole', filters.sectorRole);
  if (filters.discipline) params.set('discipline', filters.discipline);
  const res = await fetch(`${API}/clients/search?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return res.json(); // { members, externals }
}

export async function createExternalClient(data) {
  const res = await fetch(`${API}/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).client;
}

// ─── Firms ───

export async function listFirms(type) {
  const url = type ? `${API}/firms?type=${type}` : `${API}/firms`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).firms;
}

export async function createFirm(data) {
  const res = await fetch(`${API}/firms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

// ─── Save / Load ───

export async function saveProject(projectId, blob, name, metadata) {
  const form = new FormData();
  form.append('file', blob, 'project.jbz');
  if (name) form.append('name', name);
  if (metadata) form.append('metadata', JSON.stringify(metadata));

  const res = await fetch(`${API}/projects/${projectId}/save`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) throw new Error(await extractError(res));
  return res.json();
}

export async function saveDraft(projectId, blob) {
  const form = new FormData();
  form.append('file', blob, 'draft.jbz');

  const res = await fetch(`${API}/projects/${projectId}/draft`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) throw new Error(await extractError(res));
  return res.json();
}

export async function loadProject(projectId, draft = false) {
  const url = `${API}/projects/${projectId}/load${draft ? '?draft=1' : ''}`;
  const res = await fetch(url, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return res.arrayBuffer();
}

// ─── Share ───

export async function createShareLinks(projectId, partyTypes, expiresInDays = 30) {
  const res = await fetch(`${API}/projects/${projectId}/shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ partyTypes, expiresInDays }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).links;
}

export async function getShareLinks(projectId) {
  const res = await fetch(`${API}/projects/${projectId}/shares`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).links;
}

export async function resolveShareToken(token) {
  const res = await fetch(`${API}/share/${token}`);
  if (!res.ok) throw new Error(await extractError(res));
  return res.json();
}

export async function loadSharedProject(token) {
  const res = await fetch(`${API}/share/${token}/load`);
  if (!res.ok) throw new Error(await extractError(res));
  return res.arrayBuffer();
}

// ─── Note replies ───
export async function postNoteReply(projectId, noteId, body, shareToken) {
  const res = await fetch(`${API}/projects/${projectId}/notes/${noteId}/replies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ body, shareToken }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).reply;
}

export async function deleteNoteReply(projectId, noteId, replyId) {
  const res = await fetch(`${API}/projects/${projectId}/notes/${noteId}/replies/${replyId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return res.json();
}

// ─── Stakeholder share + access ───
// Tek bir paydaş için (PERSON_FIELDS / FIRM_FIELDS key'i) link oluşturur.
// accessConfig backend'e gönderilir; resolveShareToken çağrısında geri döner.
export async function createStakeholderShare(projectId, stakeholderKey, accessConfig, expiresInDays = 30) {
  const res = await fetch(`${API}/projects/${projectId}/stakeholder-shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ stakeholderKey, accessConfig, expiresInDays }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).share;
}

export async function getStakeholderShares(projectId) {
  const res = await fetch(`${API}/projects/${projectId}/stakeholder-shares`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).shares;
}

export async function revokeStakeholderShare(projectId, stakeholderKey) {
  const res = await fetch(`${API}/projects/${projectId}/stakeholder-shares/${encodeURIComponent(stakeholderKey)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json());
}

// ─── Access templates (Sablonlar > stakeholder_access tipi) ───
export async function listAccessTemplates() {
  const res = await fetch(`${API}/templates/me`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  const data = await res.json();
  const all = data.templates || data || [];
  return all.filter(t => t.type === 'stakeholder_access');
}

export async function getAccessTemplateConfig(templateId) {
  // Şablonun depodaki JSON dosyasını fileRef üzerinden çek
  const tplRes = await fetch(`${API}/templates/${templateId}`, {
    headers: authHeaders(),
  });
  if (!tplRes.ok) throw new Error(await extractError(tplRes));
  const tplData = await tplRes.json();
  const tpl = tplData.template || tplData;
  const fileRef = tpl?.fileRef;
  if (!fileRef) return null;
  const fileUrl = fileRef.startsWith('http') ? fileRef : `${API_URL}${fileRef}`;
  const fr = await fetch(fileUrl, { headers: authHeaders() });
  if (!fr.ok) return null;
  try {
    const json = await fr.json();
    return json.accessConfig || null;
  } catch { return null; }
}

// ─── Live Notes ───

export async function postNote(projectId, calloutData, shareToken) {
  const res = await fetch(`${API}/projects/${projectId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ calloutData, shareToken }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).note;
}

// ─── Attachments ───

export async function listAttachments(projectId) {
  const res = await fetch(`${API}/projects/${projectId}/attachments`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).attachments;
}

export async function uploadAttachments(projectId, category, files) {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  const res = await fetch(`${API}/projects/${projectId}/attachments/${category}`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).files;
}

export async function deleteAttachment(projectId, category, filename) {
  const res = await fetch(`${API}/projects/${projectId}/attachments/${category}/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
}

export async function getNotes(projectId, since, shareToken) {
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  if (shareToken) params.set('shareToken', shareToken);
  const res = await fetch(`${API}/projects/${projectId}/notes?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).notes;
}

/* ── Project Invitations ── */
export async function sendProjectInvitation(data) {
  const res = await fetch(`${API}/project-invitations`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return res.json();
}

export async function getProjectInvitations(projectId) {
  const res = await fetch(`${API}/project-invitations?projectId=${projectId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).invitations;
}

export async function getOrganizationInbox() {
  const res = await fetch(`${API}/project-invitations/inbox`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).invitations;
}

export async function respondToInvitation(invitationId, data) {
  const res = await fetch(`${API}/project-invitations/${invitationId}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return res.json();
}

export async function resolveOrganization(params) {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API}/organizations/resolve?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()).organizations;
}

// ─── DWG → DXF conversion ───
export async function convertDwgToDxf(dwgFile) {
  const formData = new FormData();
  formData.append('file', dwgFile);
  const res = await fetch(`${API}/convert/dwg-to-dxf`, {
    method: 'POST',
    headers: authHeaders(), // no Content-Type — browser sets multipart boundary
    body: formData,
  });
  if (!res.ok) {
    const err = await extractError(res);
    throw new Error(err);
  }
  return await res.blob(); // returns DXF as blob
}

export async function getConvertStatus() {
  const res = await fetch(`${API}/convert/status`, { headers: authHeaders() });
  if (!res.ok) return { odaAvailable: false };
  return res.json();
}
